
// pages/karigars/KarigarForm.jsx

import { useEffect, useState } from "react";
import {
  karigarsAPI,
  StitchAPI,
  getCmId,
} from "../../services/api";
import { useRouter } from "../../contexts/RouterContext";
import { useToast } from "../../contexts/ToastContext";
import {
  Breadcrumb,
  Loading,
  Spinner,
} from "../../components/ui";

const MEASUREMENT_LIMIT = 12;

const BLANK = {
  name: "",
  phone: "",
  workType: "",
  speciality: "",
};

const getMeasurementLabels = (product) =>
  Array.from(
    { length: MEASUREMENT_LIMIT },
    (_, i) => product[`PM_M${i + 1}`] || ""
  ).filter((label) => label.trim() !== "");

export default function KarigarForm({ editId }) {
  const { navigate, back } = useRouter();
  const toast = useToast();
  const isEdit = !!editId;

  const [form, setForm] = useState(BLANK);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [apiError, setApiError] = useState("");

  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState({});

  // --------------------------------------------------
  // FORM INPUT HANDLING
  // --------------------------------------------------

  const setF = (key) => (e) => {
    setForm((prev) => ({
      ...prev,
      [key]: e.target.value,
    }));

    if (errors[key]) {
      setErrors((prev) => ({
        ...prev,
        [key]: "",
      }));
    }

    setApiError("");
  };

  // --------------------------------------------------
  // LOAD KARIGAR DETAILS FOR EDIT
  // --------------------------------------------------

  useEffect(() => {
    if (!isEdit || !editId) {
      setFetching(false);
      return;
    }

    let cancelled = false;

    const fetchKarigar = async () => {
      try {
        setFetching(true);
        setApiError("");

        const response = await karigarsAPI.get(editId);

        console.log("[KarigarForm] API Response:", response);

        if (cancelled) return;

        if (response?.status !== "success") {
          throw new Error(
            response?.message || "Failed to load karigar details."
          );
        }

        const ledger = response.ledgerDetails;

        if (!ledger) {
          throw new Error("Ledger details not found.");
        }

        // Populate karigar information
        setForm({
          name: ledger.LM_Name || "",
          phone: ledger.LM_ContactNo || "",

          // LM_City is used to store Address
          workType: ledger.LM_City || "",

          // LM_ReferenceName is used to store Remarks
          speciality: ledger.LM_ReferenceName || "",
        });

        // Populate existing stitch types and rates
        const productDetails = response.productDetails || [];
        const selectedMap = {};

        productDetails.forEach((item) => {
          const pmId = Number(item.PM_ID);

          selectedMap[pmId] = {
            rate: String(item.KRD_Rate ?? ""),
          };
        });

        setSelectedProducts(selectedMap);

        console.log("[KarigarForm] Loaded Products:", selectedMap);

      } catch (error) {
        console.error("[KarigarForm] Load error:", error);

        if (!cancelled) {
          setApiError(
            `Failed to load karigar: ${error.message}`
          );
        }
      } finally {
        if (!cancelled) {
          setFetching(false);
        }
      }
    };

    fetchKarigar();

    return () => {
      cancelled = true;
    };
  }, [editId, isEdit]);

  // --------------------------------------------------
  // LOAD STITCH TYPES
  // --------------------------------------------------

  useEffect(() => {
    let cancelled = false;

    const fetchStitchTypes = async () => {
      try {
        const response = await StitchAPI.get();

        if (!cancelled) {
          setProducts(response.stitchTypes ?? []);
        }
      } catch (error) {
        console.error("[KarigarForm] Stitch list error:", error);

        if (!cancelled) {
          setProducts([]);
        }
      }
    };

    fetchStitchTypes();

    return () => {
      cancelled = true;
    };
  }, []);

  // --------------------------------------------------
  // SELECT / UNSELECT STITCH TYPE
  // --------------------------------------------------

  const toggleProduct = (pmId) => {
    setSelectedProducts((prev) => {
      const updated = { ...prev };

      if (updated[pmId]) {
        delete updated[pmId];
      } else {
        updated[pmId] = {
          rate: "",
        };
      }

      return updated;
    });
  };

  // --------------------------------------------------
  // UPDATE STITCH RATE
  // --------------------------------------------------

  const updateRate = (pmId, value) => {
    setSelectedProducts((prev) => ({
      ...prev,
      [pmId]: {
        rate: value,
      },
    }));
  };

  // --------------------------------------------------
  // VALIDATION
  // --------------------------------------------------

  const validate = () => {
    const validationErrors = {};

    if (!form.name.trim()) {
      validationErrors.name = "Karigar name is required";
    }

    if (!form.phone.trim()) {
      validationErrors.phone = "Phone number is required";
    } else if (
      !/^\d{10}$/.test(form.phone.replace(/\s/g, ""))
    ) {
      validationErrors.phone = "Enter a valid 10-digit phone number";
    }

    return validationErrors;
  };

  // --------------------------------------------------
  // SAVE KARIGAR
  // --------------------------------------------------

  const handleSubmit = async () => {
    const validationErrors = validate();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const cmId = getCmId();

    if (!cmId) {
      setApiError(
        "Company not linked. Please log out and log back in."
      );
      return;
    }

    setLoading(true);
    setApiError("");

    try {
      const Karigar_Detail = Object.entries(
        selectedProducts
      ).map(([pmId, value]) => ({
        KRD_PM_ID: Number(pmId),
        KRD_Rate: Number(value.rate || 0),
      }));

      const payload = {
        name: form.name,
        phone: form.phone,

        // Address is stored in LM_City
        workType: form.workType,

        // Remarks are stored in LM_ReferenceName
        speciality: form.speciality,

        Karigar_Detail,
      };

      console.log("[KarigarForm] Save Payload:", payload);

      if (isEdit) {
        await karigarsAPI.update(editId, payload);
        toast("Karigar updated successfully!");
      } else {
        await karigarsAPI.create(payload);
        toast("Karigar added successfully!");
      }

      back();

    } catch (error) {
      console.error("[KarigarForm] Save error:", error);
      setApiError(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------------------------
  // LOADING SCREEN
  // --------------------------------------------------

  if (fetching) {
    return (
      <div className="page">
        <Loading />
      </div>
    );
  }

  // --------------------------------------------------
  // FORM UI
  // --------------------------------------------------

  return (
    <div className="page">

      <Breadcrumb
        items={[
          {
            label: "Karigars",
            onClick: () => navigate("karigars"),
          },
          {
            label: isEdit ? "Edit Karigar" : "Add Karigar",
          },
        ]}
      />

      <div className="page-header">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <button
            className="btn btn-ghost"
            onClick={back}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 14px",
            }}
          >
            ←
          </button>

          <div>
            <h1 className="page-title">
              {isEdit ? "Edit Karigar" : "Add New Karigar"}
            </h1>

            <p className="page-sub">
              {isEdit
                ? "Update the karigar's details below"
                : "Fill in the details to register a new karigar"}
            </p>
          </div>
        </div>
      </div>

      {/* KARIGAR INFORMATION */}

      <div className="card" style={{ maxWidth: 680 }}>
        <div
          className="form-section-header"
          style={{ marginBottom: 18 }}
        >
          <span>🧵 Karigar Information</span>
        </div>

        {apiError && (
          <div
            style={{
              background: "var(--red-dim)",
              border: "1px solid rgba(224,85,85,0.3)",
              borderRadius: "var(--radius-sm)",
              padding: "12px 14px",
              marginBottom: 16,
              fontSize: 13,
              color: "var(--red)",
              lineHeight: 1.6,
            }}
          >
            {apiError}
          </div>
        )}

        <div className="form-grid">

          {/* NAME */}

          <div className="form-group">
            <label className="form-label">
              Karigar Name{" "}
              <span className="form-required">*</span>
            </label>

            <input
              className={`form-input${errors.name ? " error" : ""}`}
              value={form.name}
              onChange={setF("name")}
              placeholder="Enter karigar name"
            />

            {errors.name && (
              <span className="form-error">
                {errors.name}
              </span>
            )}
          </div>

          {/* PHONE */}

          <div className="form-group">
            <label className="form-label">
              Phone Number{" "}
              <span className="form-required">*</span>
            </label>

            <input
              className={`form-input${errors.phone ? " error" : ""}`}
              value={form.phone}
              onChange={setF("phone")}
              placeholder="10-digit mobile number"
              maxLength={10}
            />

            {errors.phone && (
              <span className="form-error">
                {errors.phone}
              </span>
            )}
          </div>

          {/* ADDRESS - TEXT FIELD */}

          <div className="form-group">
            <label className="form-label">
              Address
            </label>

            <input
              type="text"
              className="form-input"
              value={form.workType}
              onChange={setF("workType")}
              placeholder="Enter karigar address"
            />
          </div>

          {/* REMARKS - TEXT FIELD */}

          <div className="form-group">
            <label className="form-label">
              Remarks
            </label>

            <input
              type="text"
              className="form-input"
              value={form.speciality}
              onChange={setF("speciality")}
              placeholder="Enter remarks"
            />
          </div>

        </div>
      </div>

      {/* STITCH TYPES AND RATES */}

      <div
        className="card"
        style={{
          maxWidth: 680,
          marginTop: 20,
        }}
      >
        <div
          className="form-section-header"
          style={{ marginBottom: 14 }}
        >
          <span>✂️ Stitch Types &amp; Rates</span>
        </div>

        <p
          className="page-sub"
          style={{
            marginTop: -8,
            marginBottom: 16,
          }}
        >
          Pick the stitch types this karigar works on,
          and the rate you pay them for each.
        </p>

        {products.length === 0 ? (
          <div
            style={{
              color: "var(--text-muted)",
              fontSize: 13.5,
            }}
          >
            No stitch types configured yet.
            Add some in Settings first.
          </div>
        ) : (
          <div className="table-wrap">
            <table style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th style={{ width: 40 }}></th>
                  <th>Stitch Type</th>
                  <th>Measurements Tracked</th>
                  <th style={{ width: 140 }}>Rate (₹)</th>
                </tr>
              </thead>

              <tbody>
                {products.map((product) => {
                  const pmId = Number(product.PM_ID);
                  const selected = selectedProducts[pmId];
                  const labels = getMeasurementLabels(product);

                  return (
                    <tr key={pmId}>
                      <td>
                        <input
                          type="checkbox"
                          checked={!!selected}
                          onChange={() => toggleProduct(pmId)}
                        />
                      </td>

                      <td style={{ fontWeight: 600 }}>
                        {product.PM_Name}
                      </td>

                      <td
                        className="td-muted"
                        style={{ fontSize: 12.5 }}
                      >
                        {labels.length > 0
                          ? labels.join(", ")
                          : "—"}
                      </td>

                      <td>
                        {selected && (
                          <input
                            type="number"
                            className="form-input"
                            style={{ width: 110 }}
                            value={selected.rate}
                            onChange={(e) =>
                              updateRate(pmId, e.target.value)
                            }
                            placeholder="0"
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ACTION BUTTONS */}

      <div
        style={{
          display: "flex",
          gap: 10,
          justifyContent: "flex-end",
          marginTop: 20,
          maxWidth: 680,
        }}
      >
        <button
          className="btn btn-ghost"
          onClick={() => navigate("karigars")}
          disabled={loading}
        >
          Cancel
        </button>

        <button
          className="btn btn-primary btn-lg"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner /> Saving…
            </>
          ) : isEdit ? (
            "Save Changes"
          ) : (
            "Add Karigar →"
          )}
        </button>
      </div>

    </div>
  );
}
